import { useCallback, useRef } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { SearchFilter } from '../../filters/components/SearchFilter';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { PlaylistItemSearchProvider, usePlaylistItemSearch } from './PlaylistItemSearchManager';
import { PlaylistItemSearchResults } from './PlaylistItemSearchResults';
import { AddRemovePlaylistSearchItemButton } from './PlaylistSearchItemButtons';
import { PlaylistSearchFiltersBar } from '../../../containers/TracksPage/PlaylistSearchFilters';
import { PlaylistMobileFilterButton } from './PlaylistMobileFilter';
import { FILTER_TYPES } from '../../../constants/TopFilterConstants';
// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../../../hooks/searchParamHooks';
import UsageTracker from '../../../utils/UsageTracker';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { Spacer } from 'foundations-components/components/Spacer';
import I18n from 'I18n';
import Raven from 'raven-js';
import { usePlaylistItemsSearch } from '../playlistItemsHooks';
import { isInternalContent } from 'academy-common-lib/features/learningContent/utils';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ScrollableBodyContainer = ({
  children
}) => {
  const {
    isMobile
  } = useMediaQuery();
  if (isMobile) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  return /*#__PURE__*/_jsx(ScrollContainer, {
    maxHeight: "calc(80vh - 120px)",
    scrollDirection: "vertical",
    children: children
  });
};
const PlaylistItemsChangeSummary = ({
  itemsAddedCount,
  itemsRemovedCount
}) => {
  const itemsOnlyAdded = itemsAddedCount > 0 && itemsRemovedCount === 0;
  const itemsOnlyRemoved = itemsAddedCount === 0 && itemsRemovedCount > 0;
  const noChanges = itemsAddedCount === 0 && itemsRemovedCount === 0;
  if (noChanges) {
    return null;
  }
  if (itemsOnlyAdded) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.savedItems.playlistDetails.addContentToPlaylistModal.itemsAdded",
      options: {
        count: itemsAddedCount
      }
    });
  }
  if (itemsOnlyRemoved) {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.savedItems.playlistDetails.addContentToPlaylistModal.itemsRemovedStandalone",
      options: {
        count: itemsRemovedCount
      }
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.savedItems.playlistDetails.addContentToPlaylistModal.itemsAdded",
      options: {
        count: itemsAddedCount
      }
    }), ', ', /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.savedItems.playlistDetails.addContentToPlaylistModal.itemsRemoved",
      options: {
        count: itemsRemovedCount
      }
    })]
  });
};
const AddItemsToPlaylistModalInner = ({
  onClose
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const [, setSearchParam] = useSearchParam(FILTER_TYPES.SEARCH);
  const searchAreaRef = useRef(null);
  const {
    saveNewPlaylistItems,
    resetAllState,
    itemsAdded,
    itemsAddedCount,
    itemsRemoved,
    itemsRemovedCount,
    updatePlaylistLoading
  } = usePlaylistItemSearch();
  const {
    loading: isSearchLoading,
    searchCriteria,
    isAnyFilterSelected
  } = usePlaylistItemsSearch();
  const filtersOrSearchApplied = searchCriteria.search || isAnyFilterSelected;
  const playlistHasChanges = itemsAddedCount > 0 || itemsRemovedCount > 0;
  const clearSearch = useCallback(() => {
    setSearchParam([]);
  }, [setSearchParam]);
  const handleClose = useCallback(() => {
    clearSearch();
    onClose();
  }, [clearSearch, onClose]);
  const handleSearchPageChange = useCallback(() => {
    var _searchAreaRef$curren;
    (_searchAreaRef$curren = searchAreaRef.current) === null || _searchAreaRef$curren === void 0 || _searchAreaRef$curren.scrollIntoView({
      behavior: 'smooth'
    });
  }, []);
  const handleUpdatePlaylist = () => {
    const customContentAdded = itemsAdded.some(isInternalContent);
    const customContentRemoved = itemsRemoved.some(isInternalContent);
    saveNewPlaylistItems().then(result => {
      if (result !== null && result !== void 0 && result.errors) {
        return;
      }
      UsageTracker.trackStandalone('Playlist Interaction', {
        action: 'save-item-changes',
        location: 'add-items-to-playlist-modal'
      });
      if (customContentAdded) {
        UsageTracker.trackStandalone('Playlist Interaction', {
          action: 'add-custom-content-to-playlist',
          location: 'add-items-to-playlist-modal'
        });
      }
      if (customContentRemoved) {
        UsageTracker.trackStandalone('Playlist Interaction', {
          action: 'remove-custom-content-from-playlist',
          location: 'add-items-to-playlist-modal'
        });
      }
      handleClose();
    }).catch(error => {
      Raven.captureException(error);
    });
  };
  const handleCancel = useCallback(() => {
    resetAllState();
    handleClose();
    UsageTracker.track('playlistInteraction', {
      action: 'cancel-item-changes',
      location: 'add-items-to-playlist-modal'
    });
  }, [handleClose, resetAllState]);
  return /*#__PURE__*/_jsxs(Modal, {
    "data-test-id": "add-items-to-playlist-modal",
    width: 1100,
    children: [/*#__PURE__*/_jsxs(DialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.savedItems.playlistDetails.addContentToPlaylistModal.title"
        })
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: handleClose
      })]
    }), /*#__PURE__*/_jsx(DialogBody, {
      padding: "600",
      children: /*#__PURE__*/_jsx(ScrollableBodyContainer, {
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          minBlockSize: "600px",
          children: [/*#__PURE__*/_jsx("div", {
            ref: searchAreaRef,
            children: /*#__PURE__*/_jsx(Flex, {
              alignItems: "end",
              gap: "200",
              children: /*#__PURE__*/_jsx(FormControl, {
                inlineSize: "100%",
                label: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.savedItems.playlistDetails.addContentToPlaylistModal.searchLabel"
                }),
                children: /*#__PURE__*/_jsxs(Flex, {
                  alignItems: "center",
                  children: [/*#__PURE__*/_jsx(SearchFilter, {
                    appearsIn: "add-items-to-playlist-modal",
                    placeholder: I18n.text('learningCenter.savedItems.playlistDetails.search')
                  }), !isMobile && /*#__PURE__*/_jsx(View, {
                    inlineSize: "24px",
                    marginInlineStart: "200",
                    children: isSearchLoading && /*#__PURE__*/_jsx(LoadingSpinner, {
                      margin: "0",
                      size: "xs"
                    })
                  }), isMobile && /*#__PURE__*/_jsxs(_Fragment, {
                    children: [/*#__PURE__*/_jsx(Spacer, {
                      marginInlineStart: "200"
                    }), /*#__PURE__*/_jsx(PlaylistMobileFilterButton, {})]
                  })]
                })
              })
            })
          }), !isMobile && /*#__PURE__*/_jsx(PlaylistSearchFiltersBar, {}), /*#__PURE__*/_jsx(PlaylistItemSearchResults, {
            AddRemoveButton: AddRemovePlaylistSearchItemButton,
            onSearchPageChange: handleSearchPageChange
          })]
        })
      })
    }), /*#__PURE__*/_jsx(DialogFooter, {
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        flexDirection: isMobile ? 'column-reverse' : 'row',
        gap: "300",
        inlineSize: "100%",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsxs(ButtonWrapper, {
          children: [/*#__PURE__*/_jsx(LoadingButton, {
            disabled: !playlistHasChanges && !filtersOrSearchApplied,
            loading: updatePlaylistLoading,
            onClick: handleUpdatePlaylist,
            use: "primary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.savedItems.playlistDetails.addContentToPlaylistModal.updatePlaylist"
            })
          }), /*#__PURE__*/_jsx(Button, {
            onClick: handleCancel,
            use: "secondary",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.common.cancel"
            })
          })]
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-125",
          children: /*#__PURE__*/_jsx(PlaylistItemsChangeSummary, {
            itemsAddedCount: itemsAddedCount,
            itemsRemovedCount: itemsRemovedCount
          })
        })]
      })
    })]
  });
};
export const AddItemsToPlaylistModal = ({
  isOpen,
  onClose,
  playlistId
}) => {
  if (!isOpen) return null;
  return /*#__PURE__*/_jsx(PlaylistItemSearchProvider, {
    playlistId: playlistId,
    children: /*#__PURE__*/_jsx(AddItemsToPlaylistModalInner, {
      onClose: onClose
    })
  });
};